import { ActionIcon, Group, Image, SegmentedControl, Select, Text, Tooltip } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import dayjs from "dayjs";
import { SWRConfig } from "swr";
import { applyDashboardRangeField } from "../../utils/dashboardDateRange";
import { DASHBOARD_ADMIN_SWR_CONFIG } from "../../utils/dashboardQueryKeys";
import useDashboardFilters from "../../hooks/useDashboardFilters";
import AdminOverviewTab from "./AdminOverviewTab";
import AdminRevenueTab from "./AdminRevenueTab";
import AdminOperatorTab from "./AdminOperatorTab";
import AdminPlansTab from "./AdminPlansTab";
import classes from "./AdminDashboardView.module.css";

const AdminDashboardView = () => {
  const {
    activeTab,
    setActiveTab,
    timeFrame,
    setTimeFrame,
    scope,
    setScope,
    planType,
    setPlanType,
    filters,
    setFilters,
    localFilters,
    setLocalFilters,
  } = useDashboardFilters();

  return (
    <div className={classes.page}>
      <div className={classes.toolbar}>
        <div className={classes.tabsRow}>
          {["Overview", "Revenue", "Operator", "Plans"].map((tab) => (
            <button
              key={tab}
              className={`${classes.tabBtn} ${activeTab === tab ? classes.tabBtnActive : ""}`}
              onClick={() => setActiveTab(tab)}
              type="button"
            >
              {tab}
            </button>
          ))}
        </div>

        <div className={classes.toolbarGrow} />

        <Group gap={6} wrap="wrap" className={classes.filterPill}>
          {activeTab === "Plans" ? (
            <Select
              size="xs"
              className={classes.compactSelect}
              placeholder="Plan type"
              data={[
                { label: "All Plans", value: "all" },
                { label: "Mother", value: "mother" },
                { label: "Master", value: "master" },
                { label: "Customized", value: "customized" },
              ]}
              value={planType}
              onChange={(value) => setPlanType(value || "all")}
              allowDeselect
            />
          ) : (
            <SegmentedControl
              size="xs"
              className={classes.segment}
              value={timeFrame}
              onChange={setTimeFrame}
              disabled={Boolean(filters.startDate || filters.endDate)}
              data={[
                { label: "Day", value: "D" },
                { label: "Week", value: "W" },
                { label: "Month", value: "M" },
              ]}
            />
          )}

          <div className={classes.divider} />

          <Select
            size="xs"
            className={classes.compactSelect}
            placeholder="Scope"
            data={[
              { label: "Both", value: "both" },
              { label: "Domestic", value: "domestic" },
              { label: "International", value: "international" },
            ]}
            value={scope || "both"}
            onChange={(value) => setScope(value || "both")}
            allowDeselect={false}
          />

          <div className={classes.divider} />

          <Group gap={6} wrap="nowrap" className={classes.dateRange}>
            <DateInput
              value={filters.startDate}
              onChange={(value) =>
                setFilters((prev) => applyDashboardRangeField(prev, "startDate", value))
              }
              placeholder="From"
              size="xs"
              className={classes.compactDate}
              maxDate={new Date()}
            />
            <Text className={classes.arrow}>–</Text>
            <DateInput
              value={filters.endDate}
              onChange={(value) =>
                setFilters((prev) => applyDashboardRangeField(prev, "endDate", value))
              }
              placeholder="To"
              size="xs"
              className={classes.compactDate}
              maxDate={new Date()}
              minDate={
                filters.startDate
                  ? dayjs(filters.startDate).startOf("day").toDate()
                  : undefined
              }
            />
            {(filters.startDate || filters.endDate) && (
              <Tooltip label="Reset dates" position="bottom" withArrow fz={11}>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="xs"
                  onClick={() => setFilters({ startDate: null, endDate: null })}
                  aria-label="Reset date filter"
                >
                  <Image src="/images/svg/cross.svg" w={10} h={10} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Group>
      </div>

      <SWRConfig value={DASHBOARD_ADMIN_SWR_CONFIG}>
        <div className={classes.content}>
          {activeTab === "Overview" ? (
            <AdminOverviewTab
              filters={filters}
              localFilters={localFilters}
              setLocalFilters={setLocalFilters}
              timeFrame={timeFrame}
              scope={scope}
            />
          ) : null}
          {activeTab === "Revenue" ? (
            <AdminRevenueTab filters={filters} timeFrame={timeFrame} scope={scope} />
          ) : null}
          {activeTab === "Operator" ? (
            <AdminOperatorTab filters={filters} timeFrame={timeFrame} scope={scope} />
          ) : null}
          {activeTab === "Plans" ? (
            <AdminPlansTab filters={filters} scope={scope} planType={planType} />
          ) : null}
        </div>
      </SWRConfig>
    </div>
  );
};

export default AdminDashboardView;
